import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CopySimple, CheckCircle, Bank, QrCode, WhatsappLogo, Info, CreditCard, LockSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { api, inr } from "@/lib/api";
import { CONTACT } from "@/data/site";

const BANK = [
  { label: "A/c Name", value: "Cluetrip Holidays Pvt Ltd", id: "name" },
  { label: "A/c Number", value: "201037186714", id: "account" },
  { label: "IFSC Code", value: "INDB0000817", id: "ifsc" },
  { label: "Bank", value: "IndusInd Bank", id: "bank" },
  { label: "Branch", value: "Vaishali, Ghaziabad", id: "branch" },
];

function fallbackCopy(text, done) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); done(); } catch (e) { toast.error("Copy failed — please copy manually."); }
  document.body.removeChild(ta);
}

function BankRow({ label, value, id }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const done = () => {
      setCopied(true);
      toast.success(`${label} copied!`);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(done).catch(() => fallbackCopy(value, done));
    } else {
      fallbackCopy(value, done);
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-brand-cream px-4 py-3.5 border border-brand-sand">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#8B89A4]">{label}</p>
        <p className="font-accent font-bold text-brand-navy text-base sm:text-lg break-all">{value}</p>
      </div>
      <button
        onClick={copy}
        data-testid={`copy-${id}`}
        aria-label={`Copy ${label}`}
        className="shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal transition-colors hover:bg-brand-teal hover:text-white"
      >
        {copied ? <CheckCircle size={20} weight="fill" /> : <CopySimple size={20} />}
      </button>
    </div>
  );
}

function PayOnlineCard() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: params.get("name") || "",
    email: "",
    phone: params.get("phone") || "",
    amount: params.get("amount") || "",
    purpose: params.get("purpose") || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full rounded-xl border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/payments/create-custom", { ...form, amount: parseInt(form.amount, 10) });
      const f = document.createElement("form");
      f.method = "POST";
      f.action = r.data.action;
      Object.entries(r.data.params).forEach(([k, v]) => {
        const i = document.createElement("input");
        i.type = "hidden"; i.name = k; i.value = v;
        f.appendChild(i);
      });
      document.body.appendChild(f);
      f.submit();
    } catch {
      toast.error("Payment start nahi ho paya. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="rounded-3xl bg-white p-7 md:p-9 shadow-[0_12px_40px_rgba(46,42,128,0.12)] ring-2 ring-brand-orange/60 relative" data-testid="payu-card">
      <span className="absolute -top-3 left-7 rounded-full bg-brand-orange px-4 py-1 text-[11px] font-extrabold uppercase tracking-wider text-white">Recommended · Instant</span>
      <div className="flex items-center gap-3 mb-5">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
          <CreditCard size={26} weight="duotone" />
        </span>
        <div>
          <h2 className="font-heading text-2xl font-semibold text-brand-navy">Pay Online via PayU</h2>
          <p className="text-xs text-[#8B89A4]">Credit Card · Debit Card · UPI · NetBanking · Wallets · EMI</p>
        </div>
      </div>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
        <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="payu-name" className={inputCls} />
        <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="payu-email" className={inputCls} />
        <input required value={form.phone} onChange={set("phone")} placeholder="Phone (10 digits)" pattern="[0-9+ -]{10,15}" data-testid="payu-phone" className={inputCls} />
        <input required type="number" min="1" max="1000000" value={form.amount} onChange={set("amount")} placeholder="Amount (₹)" data-testid="payu-amount" className={inputCls} />
        <input value={form.purpose} onChange={set("purpose")} placeholder="Package / purpose (optional)" data-testid="payu-purpose" className={`${inputCls} sm:col-span-2`} />
        <button type="submit" disabled={loading} data-testid="payu-submit"
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-4 text-sm font-bold uppercase tracking-wide text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
          <CreditCard size={18} weight="fill" /> {loading ? "Redirecting to PayU..." : form.amount ? `Pay ${inr(parseInt(form.amount, 10) || 0)} Securely` : "Pay Securely"}
        </button>
        <p className="sm:col-span-2 flex items-center justify-center gap-1.5 text-[11px] text-[#8B89A4]">
          <LockSimple size={12} weight="fill" /> 100% secure payment powered by PayU
        </p>
      </form>
    </div>
  );
}

export default function Pay() {
  const whatsappConfirm = () => {
    const msg = "Hi Cluetrip! Maine payment kar di hai. Payment screenshot bhej raha/rahi hoon. Please confirm my booking.";
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  return (
    <div data-testid="pay-page">
      {/* BANNER */}
      <section className="relative bg-brand-navy py-16 overflow-hidden grain">
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 text-center">
          <h1 className="text-4xl sm:text-6xl font-heading font-semibold text-white">Pay Now</h1>
          <p className="mt-3 text-white/75 font-body">Card / UPI / NetBanking se online pay karein — ya Bank Transfer / QR scan</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-10 pt-14">
        <Reveal>
          <PayOnlineCard />
        </Reveal>
        <div className="my-10 flex items-center gap-4">
          <span className="h-px flex-1 bg-brand-sand" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B89A4]">Ya phir direct transfer karein</span>
          <span className="h-px flex-1 bg-brand-sand" />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-10 pb-14">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* BANK DETAILS */}
          <Reveal>
            <div className="rounded-3xl bg-white p-7 md:p-9 shadow-[0_12px_40px_rgba(46,42,128,0.12)]" data-testid="bank-details-card">
              <div className="flex items-center gap-3 mb-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
                  <Bank size={26} weight="duotone" />
                </span>
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-brand-navy">Bank Transfer</h2>
                  <p className="text-xs text-[#8B89A4]">NEFT / IMPS / RTGS accepted</p>
                </div>
              </div>
              <div className="space-y-3">
                {BANK.map((b) => <BankRow key={b.id} {...b} />)}
              </div>
            </div>
          </Reveal>

          {/* QR */}
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-white p-7 md:p-9 shadow-[0_12px_40px_rgba(46,42,128,0.12)] text-center" data-testid="qr-card">
              <div className="flex items-center justify-center gap-3 mb-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
                  <QrCode size={26} weight="duotone" />
                </span>
                <div className="text-left">
                  <h2 className="font-heading text-2xl font-semibold text-brand-navy">Scan &amp; Pay (UPI)</h2>
                  <p className="text-xs text-[#8B89A4]">PhonePe · Google Pay · Paytm · Any UPI app</p>
                </div>
              </div>
              <img
                src="/assets/phonepe-qr.png"
                alt="Cluetrip Holidays PhonePe QR Code"
                data-testid="qr-image"
                className="mx-auto w-full max-w-[320px] rounded-2xl border border-brand-sand shadow-md"
              />
              <p className="mt-4 font-accent font-bold text-brand-navy uppercase tracking-wide text-sm">Cluetrip Holidays Pvt Ltd</p>
            </div>
          </Reveal>
        </div>

        {/* AFTER PAYMENT */}
        <Reveal delay={0.15}>
          <div className="mt-10 rounded-3xl bg-gradient-to-r from-brand-teal to-brand-tealdark p-7 md:p-9 text-white flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <Info size={28} weight="duotone" className="shrink-0 mt-0.5 text-yellow-300" />
              <div>
                <h3 className="font-heading text-2xl font-semibold text-white">Payment done?</h3>
                <p className="mt-1 text-white/85 font-body text-sm max-w-xl">
                  Payment karne ke baad screenshot WhatsApp par bhejein — hum turant aapki booking confirm kar denge. Kisi bhi sawaal ke liye call karein: {CONTACT.phones[0]}
                </p>
              </div>
            </div>
            <button
              onClick={whatsappConfirm}
              data-testid="pay-whatsapp-confirm"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-extrabold uppercase tracking-wide text-white shadow-xl transition-transform hover:-translate-y-0.5"
            >
              <WhatsappLogo size={20} weight="fill" /> Send Payment Proof
            </button>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
